<template>
  <div class="d-flex align-center ml-2 mb-2">
    <span class="text-h6">{{ $t('HOME.MY_CURRENT_PROJECTS') }}</span>
    <v-tooltip max-width="250" content-class="line-height-normal">
      <template #activator="{ props }">
        <v-icon color="info" size="small" class="ml-2" v-bind="props">far fa-question-circle</v-icon>
      </template>
      <span>{{ $t('HOME.PROJECTS_EXPLANATIONS') }}</span>
    </v-tooltip>
    <router-link to="/projects" class="ml-auto">{{ $t('HOME.SEE_ALL_PROJECTS') }}</router-link>
  </div>
  <v-card v-if="! loaded" class="flex-center-center" style="font-size:18px; height: 240px"><i class="far fa-spinner fa-spin fa-2x fa-fw"></i></v-card>
  <v-card v-else-if="! selectedProject" class="pa-2 overflow-y-auto overflow-x-hidden light-scrollbar d-flex flex-column" style="min-height: calc(50vh - 96px); height: 240px">
    <table v-if="planningsByPriority.length" class="w-100">
      <tbody>
        <tr v-for="project in planningsByPriority" :key="project.id" class="line-height-normal">
          <td class="py-1 pr-2">
            <v-img :src="project.meta.imgurl" :width="48" :max-height="40" cover position="0 0"></v-img>
          </td>
          <td class="py-1 pr-1">
            <router-link :to="project.meta.url" class="color-inherit font-weight-bold small">{{ project.getTitle() || $t('PLANNING.UNNAMED_PROJECT') }}</router-link>
          </td>
          <td v-for="tab in tabs" :key="tab.id" class="pa-1">
            <v-btn :color="tab.color(project)" :disabled="! tab.count(project)" :title="tab.title" variant="tonal" rounded="sm" block size="large"
                   @click="selectedProject = project; selectedTab = tab.id">
              <div>
                <v-icon :class="tab.icon"></v-icon>
                <div>{{ tab.count(project) }}</div>
              </div>
            </v-btn>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-else class="flex-grow-1 d-flex flex-column px-2 text-medium-emphasis">
      <!-- empty state -->
      <table class="w-100">
        <tbody>
          <tr class="line-height-normal">
            <td class="py-1 pr-2">
              <v-img :src="defaultViewBg" :width="48" :max-height="40" cover position="0 0" style="filter: grayscale(1)"></v-img>
            </td>
            <td class="py-1 pr-1">
              <div class="rounded-pill" style="height: 16px; background-color: currentColor; width: 120px; opacity: .3"></div>
            </td>
            <td v-for="tab in tabs" :key="tab.id" class="pa-1">
              <v-btn disabled :title="tab.title" variant="tonal" rounded="sm" block size="large">
                <div><v-icon :class="tab.icon"></v-icon><div>0</div></div>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="ma-auto" style="max-width: 600px">
        <div class="mt-4 mb-2 font-weight-black">{{ $t('HOME.NO_ACTIVITY') }}</div>
        <div>{{ $t('HOME.FIND_HERE') }} {{ $t('HOME.PROJECTS_EXPLANATIONS').charAt(0).toLowerCase() }}{{ $t('HOME.PROJECTS_EXPLANATIONS').slice(1) }}</div>
      </div>
    </div>
    <div v-if="! isConsultOnly" class="mt-auto">
      <div class="mt-4 mb-2">
        <router-link to="/projects#new" style="color:inherit; text-decoration: none">
          <v-btn size="large" block color="lightgrey">
            <v-icon start size="large">far fa-plus</v-icon>{{ $t('PROJECTS.START_A_NEW_PROJECT') }}
          </v-btn>
        </router-link>
      </div>
    </div>
  </v-card>
  <v-card v-else>
    <div class="bg-lightgrey d-flex align-center">
      <v-btn-icon size="small" class="mr-1" @click="selectedProject = null; selectedTab = null">
        <v-icon>fas fa-arrow-left</v-icon>
      </v-btn-icon>
      <span class="font-weight-bold text-ellipsis">{{ selectedProject.getTitle() || $t('PLANNING.UNNAMED_PROJECT') }}</span>
      <v-btn-icon :title="$t('ELEMENT-DETAIL.OPEN_PROJECT')" :to="selectedProject.meta.url" size="x-small" class="ml-1">
        <v-icon>far fa-external-link</v-icon>
      </v-btn-icon>
    </div>
    <v-tabs v-model="selectedTab" grow density="comfortable" class="bg-lightgrey">
      <v-tab v-for="tab in tabs" :key="tab.id" :value="tab.id" variant="tonal" :title="tab.title" :base-color="tab.color(selectedProject)" :disabled="! tab.count(selectedProject)">
        <v-icon size="large" :class="tab.icon" class="mr-2"></v-icon>
        <div class="align-self-baseline">{{ tab.count(selectedProject) }}</div>
      </v-tab>
    </v-tabs>

    <v-window v-model="selectedTab">
      <v-window-item v-for="tab in tabs" :key="tab.id" :value="tab.id">
        <div class="py-2 overflow-y-auto overflow-x-hidden light-scrollbar" style="min-height: calc(50vh - 96px - 84px); height: 200px">
          <home-user-notifications-list v-if="tab.id == 'notifications'" :notifications="filteredNotifications"></home-user-notifications-list>
          <home-user-comments-list v-else-if="tab.id == 'comments'" :comments="filteredComments"></home-user-comments-list>
          <home-user-actions-list v-else-if="tab.id == 'actions'" :actions="filteredActions" hide-project-title></home-user-actions-list>
          <div v-else-if="tab.id == 'elements'" class="text-center milestones-inline">
            <v-row v-for="el in filteredElements" :key="el.id">
              <v-col :class="{ strong: el.status == 'starts_soon' }" cols="2" class="nobr" style="font-size: 75%">
                {{ $filters.moment(el.getStartTime(), 'mediumDateNoYear') }}
              </v-col>
              <v-col cols="8">
                <planning-element-view :id="`el${el.id}`" :el="el" :title="el.display_title"
                                       style="cursor: default; position: relative; font-size: 75%" disable-mini-element>
                  <template #hover-menu>
                    <div class="element-menu d-flex align-end" style="left: 100%; width: 25px; color: inherit">
                      <v-btn-icon :title="$t('HOME.OPEN_IN_PROJECT')" :to="elementUrl(el)" size="x-small">
                        <v-icon>far fa-external-link</v-icon>
                      </v-btn-icon>
                    </div>
                  </template>
                </planning-element-view>
              </v-col>
              <v-col :class="{ strong: el.status == 'ends_soon' }" cols="2" class="nobr" style="font-size: 75%">
                {{ $filters.moment(el.getEndTime(), 'mediumDateNoYear') }}
              </v-col>
            </v-row>
          </div>
        </div>
      </v-window-item>
    </v-window>
  </v-card>
</template>

<script>
  import { mapState } from 'pinia';
  import useHomeUserStore from '@/stores/home-user';
  import useUsersAccessRightStore from '@/stores/users/accessRight';
  import PlanningElementView from '@/components/Planning/PlanningElementView.vue';
  import HomeUserNotificationsList from './HomeUserNotificationsList.vue';
  import HomeUserCommentsList from './HomeUserCommentsList.vue';
  import HomeUserActionsList from './HomeUserActionsList.vue';
  import defaultViewBg from '@/img/view_bg.svg';

  export default {
    components: {
      PlanningElementView,
      HomeUserNotificationsList,
      HomeUserCommentsList,
      HomeUserActionsList,
    },
    data() {
      return {
        tabs: [
          {
            id: 'notifications',
            icon: 'far fa-bell',
            title: this.$t('HOME.NOTIFICATION_ICON_EXPLANATION'),
            color: () => 'errorred',
            count: project => this.planningsDataCount[project.id].notificationsCount,
          },
          {
            id: 'comments',
            icon: 'far fa-comment',
            title: this.$t('HOME.COMMENTS_ICON_EXPLANATION'),
            color: () => 'info',
            count: project => this.planningsDataCount[project.id].commentsCount,
          },
          {
            id: 'actions',
            icon: 'far fa-check-square',
            title: this.$t('HOME.LATE_ICON_EXPLANATION'),
            color: project => (this.planningsDataCount[project.id].actionsLateCount ? 'errorred' : 'warningorange'),
            count: project => this.planningsDataCount[project.id].actionsLateCount + this.planningsDataCount[project.id].actionsUrgentCount,
          },
          {
            id: 'elements',
            icon: 'far fa-calendar-alt',
            title: this.$t('HOME.ELEMENTS_ICON_EXPLANATION'),
            color: () => 'primary',
            count: project => this.planningsDataCount[project.id].elementsCount,
          },
        ],
        selectedProject: null,
        selectedTab: null,
        defaultViewBg,
        usersAccessRightStore: useUsersAccessRightStore(),
      };
    },
    computed: {
      filteredNotifications() {
        if (! this.selectedProject) return [];
        return this.notifications.filter(notif => notif.section_type == 'planning' && notif.section_id == this.selectedProject.id);
      },
      filteredComments() {
        return this.comments.filter(comment => comment.planning_id == this.selectedProject.id);
      },
      filteredActions() {
        if (! this.selectedProject) return {};
        return [
          ...this.actionsByStatus.late.filter(action => action.getPlanningId() == this.selectedProject.id),
          ...this.actionsByStatus.urgent.filter(action => action.getPlanningId() == this.selectedProject.id),
        ];
      },
      filteredElements() {
        return this.elements.filter(el => el.getPlanning().id == this.selectedProject.id);
      },
      isConsultOnly() { return this.usersAccessRightStore.isConsultOnly; },
      ...mapState(useHomeUserStore, ['planningsByPriority', 'planningsDataCount', 'notifications', 'comments', 'actionsByStatus', 'elements', 'loaded']),
    },
    methods: {
      elementUrl(el) {
        const projectUrl = el?.getPlanning()?.meta.url;
        return projectUrl ? `${projectUrl}?el=${parseInt(el.id, 10)}` : null;
      },
    },
  };
</script>
